import React from 'react';
import Portal from './../common/Portal';
import _ from 'lodash';
import UploadingError from './UploadingError';
import ViewUtil from './../ViewUtil';
import CollectionUploadJob from '../upload/CollectionUploadJob';
import { ZmCollection } from '../autogenerated/ZoomBridge';
import AssetUploadJob from '../upload/AssetUploadJob';
import Upload from '../upload/Upload';
import { COLLECTIONS, SHOW_UPLOADS, DOWNLOAD_JOBS } from '../store/Constants';
import Store from '../store/Store';
import PollAndDownloadJob from '../protocol/PollAndDownloadJob';
import Button from '../common/Button';
import { getUploadObjectForFolder } from '../uploadings/Uploading';
import CreateFolderJob from '../upload/CreateFolderJob';
import t from '../i18n/I18n';
import { ThemeSetup } from '../themes/ThemeSetup';

import './UploadingsPopover.css';

type Props = {
  assetUploadJobs: AssetUploadJob[] | CreateFolderJob[],
  collectionUploadJobs: CollectionUploadJob[],
  pollAndDownloadJobs: PollAndDownloadJob[],
  position: Object,
  onClose: Function
};

export default class UploadingsPopover extends React.Component<Props, {}> {
  render() {
    const { assetUploadJobs, collectionUploadJobs, pollAndDownloadJobs, position, onClose } = this.props;

    return (
      <Portal onClose={onClose} defaultOpen={true} closeOnEsc={true} closeOnOutsideClick={true}>
        <div style={{ ...styles.uploadingsPopover, ...position }} name="UploadingsPopover">
          <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
            {pollAndDownloadJobs.length ? <div className="header">{t('UPLOADING.DOWNLOADS')}</div> : null}
            {_.map(pollAndDownloadJobs, (job, index) => this.renderDownloadJob(job, index))}
            {collectionUploadJobs.length ? <div className="header">{t('UPLOADING.COLLECTIONS')}</div> : null}
            {_.map(collectionUploadJobs, (job, index) => this.renderCollectionUploadJob(job, index))}
            {assetUploadJobs.length ? <div className="header">{t('UPLOADING.ASSETS')}</div> : null}
            {assetUploadJobs.length <= 5 ? (
              _.map(assetUploadJobs, (job: AssetUploadJob, index) => this.renderUploading(job, index))
            ) : (
              <div style={{ padding: '6px 14px' }}>
                {assetUploadJobs.length} {t('UPLOADING.UPLOADS')}
              </div>
            )}
          </div>
          {assetUploadJobs.length ? (
            <div className="open-uploads" onClick={() => this.openUploadings()}>
              <div style={{ margin: 'auto' }}>{t('OPEN_UPLOADS')}</div>
            </div>
          ) : null}
        </div>
      </Portal>
    );
  }

  renderDownloadJob = (job: PollAndDownloadJob, index: number) => {
    return (
      <div key={index} className="job">
        <span>{t('UPLOADING.DOWNLOADING_FILES')}</span>
        <Button
          icon={ThemeSetup.getImageURL('trash_red.svg')}
          title={t('CANCEL_DOWNLOAD')}
          onClick={() => this.onCancelDownloadClick(job)}
        />
      </div>
    );
  };

  renderCollectionUploadJob = (job: CollectionUploadJob, index: number) => {
    const collections = Store.get(COLLECTIONS);
    const collection: ZmCollection = collections.find((c: ZmCollection) => c.getCollectionId() === job.collectionId);
    return (
      <div key={index} className="collection-upload-op" onClick={() => this.onCollectionClick(job.collectionId)}>
        <span className="collection-name">{collection.getName()}</span>
        <span style={{ padding: '0 5px' }}> - </span>
        <span>
          {job.jobs.length} {t('UPLOADING.UPLOADS')}
        </span>
      </div>
    );
  };

  renderUploading(job: AssetUploadJob | CreateFolderJob, index: number) {
    const upload: Upload = job.upload || getUploadObjectForFolder(job);
    var progress = Math.floor((upload.loaded / upload.total) * 100) || 0;

    var statusMarkup;
    if (upload.status == 'FINISHED') statusMarkup = <div style={styles.finished}>{t('UPLOADING.FINISHED')}</div>;
    if (upload.status == 'UPLOADED') statusMarkup = <div style={styles.uploaded}>{t('UPLOADING.UPLOADED')}</div>;
    if (upload.status == 'ERROR') statusMarkup = <UploadingError text={upload.errorText} isPopOver />;
    if (upload.status == 'UPLOADING') {
      statusMarkup = (
        <div className="flex-main flex-row align-center">
          <div className="progress-bar-wrapper">
            <div className="progress-bar" style={{ width: `${progress}%` }} />
          </div>
          <div className="progress-percent">{progress}%</div>
        </div>
      );
    }

    return (
      <div key={index} className="asset-upload flex-row align-center">
        <div
          style={
            upload.type.indexOf('image') > -1
              ? { ...styles.preview, backgroundImage: `url(${upload.preview})` }
              : { ...styles.mock, backgroundImage: `url(${upload.preview})` }
          }
        />
        {statusMarkup}
      </div>
    );
  }

  openUploadings() {
    Store.put(SHOW_UPLOADS, true);
  }

  onCollectionClick = (collectionId) => {
    this.props.onClose();
    ViewUtil.switchViewToAddAssetsInCollection(collectionId);
  };

  onCancelDownloadClick = (job: PollAndDownloadJob) => {
    job.cancel();
    Store.put(
      DOWNLOAD_JOBS,
      this.props.pollAndDownloadJobs.filter((j) => j.id !== job.id)
    );
    this.props.onClose();
  };
}

const styles = {
  uploadingsPopover: {
    position: 'absolute',
    background: 'var(--primary-header-bg)',
    color: 'var(--primary-font-color)',
    boxShadow: '0 15px 30px -10px rgba(0,0,0,0.20)',
    zIndex: '2'
  },
  uploaded: {
    position: 'absolute',
    top: '10px',
    left: '37px',
    color: 'var(--secondary-font-color)',
    fontSize: '12px',
    backgroundImage: 'url(images/uploaded.svg)',
    backgroundRepeat: 'no-repeat',
    backgroundPosition: '75px 50%',
    width: '100px'
  },
  error: {
    position: 'absolute',
    top: '10px',
    left: '37px',
    color: 'var(--primary-box-shadow)',
    fontSize: '12px',
    width: '100px'
  },
  preview: {
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    width: '32px',
    height: '32px',
    float: 'left'
  },
  mock: {
    backgroundSize: '16px 16px',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    width: '32px',
    height: '32px',
    float: 'left'
  },
  finished: {
    position: 'absolute',
    top: '10px',
    left: '37px',
    color: 'var(--primary-font-color)',
    fontSize: '12px',
    backgroundImage: 'url(images/uploaded_green.svg)',
    backgroundRepeat: 'no-repeat',
    backgroundPosition: '64px 50%',
    width: '100px'
  }
};
